// src/hooks/useTelegramBackButton.js

import { useEffect, useRef } from "react";

const TELEGRAM_READY_EVENT = "telegram-webapp-ready";

let idSeq = 0;
let orderSeq = 0;
const registry = new Map();
let isListening = false;

function pickActiveHandler() {
  let best = null;
  for (const entry of registry.values()) {
    if (!entry.visible || typeof entry.onClick !== "function") continue;
    if (!best) {
      best = entry;
      continue;
    }
    if (
      entry.priority > best.priority ||
      (entry.priority === best.priority && entry.order > best.order)
    ) {
      best = entry;
    }
  }
  return best;
}

const globalBackHandler = () => {
  const active = pickActiveHandler();
  if (active && typeof active.onClick === "function") {
    active.onClick();
  }
};

function syncTelegramBackButton() {
  const tg = window.Telegram?.WebApp;
  if (!tg || !tg.BackButton) return;

  const active = pickActiveHandler();

  if (active) {
    tg.BackButton.show();
    if (!isListening) {
      tg.BackButton.onClick(globalBackHandler);
      isListening = true;
    }
    return;
  }

  tg.BackButton.hide();
  if (isListening && tg.BackButton.offClick) {
    tg.BackButton.offClick(globalBackHandler);
    isListening = false;
  }
}

export default function useTelegramBackButton(visible, onClick, options = {}) {
  const idRef = useRef(null);
  if (!idRef.current) {
    idRef.current = ++idSeq;
  }

  const priority =
    typeof options === "number" ? options : options?.priority ?? 0;

  useEffect(() => {
    const handleTelegramReady = () => {
      syncTelegramBackButton();
    };
    const id = idRef.current;
    registry.set(id, {
      id,
      visible: !!visible,
      onClick,
      priority,
      order: ++orderSeq,
    });
    syncTelegramBackButton();
    if (typeof window !== "undefined") {
      window.addEventListener(TELEGRAM_READY_EVENT, handleTelegramReady);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener(TELEGRAM_READY_EVENT, handleTelegramReady);
      }
      registry.delete(id);
      syncTelegramBackButton();
    };
  }, [visible, onClick, priority]);
}
